import { useCallback, useMemo } from 'react';
import { Flex } from '@banhala/ui-kit';
import { CheckboxFilterRow } from './CheckboxFilterRow';
import type {
  CheckboxFilterListProps,
  CheckboxFilterListEntity,
  CheckboxFilterRow as CheckboxFilterRowType,
} from './types';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';
import { useFilterListAtom } from '../FilterList/Provider/filterList/useFilterListAtom';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

export const CheckboxFilterList = ({
  entity,
  logging,
  scrollToFilterList,
}: CheckboxFilterListProps) => {
  // const navigation = useNavigation<
  //   NavigationProp<{
  //     [route: string]: Record<string, string[] | undefined>;
  //   }>
  // >();
  const { setFilterListAtom } = useFilterListAtom();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const rows = useMemo(
    () => entity?.map((e: CheckboxFilterListEntity) => e.item) ?? [],
    [entity],
  );

  const handleToggleItem = useCallback(
    (
      queryName: string,
      queryValue: string,
      itemText: string,
      isSelected: boolean,
      itemLogging?: IScreensLogging,
    ) => {
      // 현재 선택된 아이템들 수집
      const allItems = rows.flatMap(row =>
        row.filter_item_list.map(fi => ({
          ...fi.item,
          queryName: row.query_name,
        })),
      );
      const checkedItems = allItems.filter(
        item =>
          item.is_selected &&
          !(item.queryName === queryName && item.query_value === queryValue),
      );

      if (!isSelected) {
        checkedItems.push({
          text: itemText,
          query_value: queryValue,
          queryName,
          image: '',
          is_selected: true,
        });
      }

      const analyticsLogging = {
        ...logging?.analytics,
        ...itemLogging?.analytics,
        CHECKBOX_FILTER_COUNT: checkedItems.length,
        CHECKBOX_LIST: checkedItems.map(i => i.text),
        CHECKBOX_SNO_LIST: checkedItems.map(i => i.query_value),
        IS_SELECTED: !isSelected,
      };

      logAnalyticsEvent({
        name: 'APPLY_CHECKBOX_FILTER',
        analytics: analyticsLogging,
        ably_only_log: { ...logging?.ably_only_log, ...itemLogging?.ably_only_log },
      });

      // query param 업데이트
      const currentSelected =
        rows
          .find(r => r.query_name === queryName)
          ?.filter_item_list.filter(fi => fi.item.is_selected)
          .map(fi => fi.item.query_value) ?? [];

      const selectedSet = new Set(currentSelected);
      if (isSelected) {
        selectedSet.delete(queryValue);
      } else {
        selectedSet.add(queryValue);
      }

      const nextValue =
        selectedSet.size > 0 ? Array.from(selectedSet) : undefined;

      scrollToFilterList();
      setFilterListAtom(prev => ({
        ...prev,
        [queryName]: nextValue,
      }));
      // navigation.setParams({ [queryName]: nextValue });
    },
    [rows, logging, scrollToFilterList, setFilterListAtom, logAnalyticsEvent],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <Flex borderTopWidth={1} borderColor="border_tertiary">
      {rows.map((row: CheckboxFilterRowType, index: number) => (
        <CheckboxFilterRow
          key={`${row.query_name}-${index}`}
          row={row}
          onToggleItem={handleToggleItem}
        />
      ))}
    </Flex>
  );
};
